import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle } from "lucide-react";
import Logo from "@/components/Logo";
import SEOHead from "@/components/SEOHead";
import { CONSENT_EVENT, hasCookieConsent, trackPixelEvent } from "@/lib/metaPixel";


const accent = { fontFamily: "'Playfair Display', serif", fontStyle: "italic" } as const;
const mono = { fontFamily: "'JetBrains Mono', monospace" } as const;

export interface ThankYouPageProps {
  /** Nom de l'événement Meta à déclencher (ex: 'Schedule', 'Purchase') */
  eventName: string;
  /** Valeur content_name envoyée à Meta */
  contentName: string;
  value?: number;
  currency?: string;
  /** Déclenche aussi fbq('track', 'Lead') après l'événement principal */
  alsoTrackLead?: boolean;
  /** Titre affiché */
  title: string;
  /** Corps de texte — les sauts de ligne sont préservés */
  body: string;
  /** Page d'origine pour le bouton de retour */
  backTo: string;
  backLabel?: string;
  canonical?: string;
}

const ThankYouPage = ({
  eventName,
  contentName,
  value,
  currency,
  alsoTrackLead = false,
  title,
  body,
  backTo,
  backLabel = "Retour",
  canonical,
}: ThankYouPageProps) => {
  const [consent, setConsent] = useState(() => hasCookieConsent());
  const fired = useRef(false);

  useEffect(() => {
    const onChange = () => setConsent(hasCookieConsent());
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (!consent || fired.current) return;
    const params: Record<string, unknown> = { content_name: contentName };
    if (typeof value === "number") params.value = value;
    if (currency) params.currency = currency;
    // Le PageView est déclenché par MetaPixelRouteTracker ; on enchaîne juste après.
    const t = window.setTimeout(() => {
      if (fired.current) return;
      fired.current = true;
      trackPixelEvent(eventName, params);
      if (alsoTrackLead && eventName !== "Lead") {
        trackPixelEvent("Lead", params);
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [consent, eventName, contentName, value, currency, alsoTrackLead]);


  return (
    <>
      <SEOHead title={`${title} — Agence 8020`} description={body.slice(0, 155)} canonical={canonical} noIndex />

      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="border-b border-border">
          <div className="max-w-[1280px] mx-auto px-6 md:px-8 h-16 flex items-center">
            <Logo variant="full" size="md" />
          </div>
        </header>

        <main className="flex-1 relative overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          </div>

          <section className="relative w-full max-w-[680px] mx-auto px-6 md:px-8 py-20 md:py-28 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 border border-primary/20 mb-8"
            >
              <CheckCircle className="w-8 h-8 md:w-10 md:h-10 text-primary" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-[3.45rem] md:text-[5.75rem] leading-[1.05] tracking-tight mb-8 text-primary"
              style={accent}
            >
              {title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-foreground/85 leading-relaxed whitespace-pre-line"
            >
              {body}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-14"
            >
              <Link
                to={backTo}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {backLabel}
              </Link>
            </motion.div>
          </section>
        </main>

        <footer className="border-t border-border">
          <div
            className="max-w-[1280px] mx-auto px-6 md:px-8 py-8 text-center text-xs text-muted-foreground"
            style={mono}
          >
            © {new Date().getFullYear()} Agence 8020
          </div>
        </footer>
      </div>
    </>
  );
};

export default ThankYouPage;
